import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Icon from '@material-ui/core/Icon';
import Collapse from '@material-ui/core/Collapse';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';

import { Filter, Params, EadNode } from 'types';

import { GridLeftPanelItemProps } from '../grid-types';
import { useFetchList } from '../grid-hooks';
import { GridLeftPanelLoadMore } from './grid-left-panel-load-more';
import { GridLeftPanelEADItem } from './grid-left-panel-ead-item';
import { trimLabel } from '../upper-panel/grid-upper-panel';

const addFilter = (params: Params, filter: Filter) => {
  const newParams = { ...params };
  const newFilters =
    newParams.filters?.filter(({ field }) => field !== filter.field) ?? [];

  newFilters.push(filter);
  newParams.filters = newFilters;

  return newParams;
};

export function GridLeftPanelItem(props: GridLeftPanelItemProps) {
  const {
    rootArchivalAid,
    classes,
    item,
    GridLeftPanelItem,
    level = 0,
    gridConfig = [],
    hierarchyPath = [],
    archivalAidLevel = 0,
  } = props;

  const gridEntityConfig = gridConfig[level];
  const { getLabel, getId, getChildrenFilter } = gridEntityConfig;

  const itemId = getId(item);
  const itemLabel = getLabel(item);
  const itemLink = itemId ? `${gridConfig[0].urlRoute}/${level}/${itemId}` : '';
  const inHierarchyPath = hierarchyPath.some((i) => i.id === itemId);

  const gridChildrenConfig = gridConfig?.[level + 1];
  const { listApi, params: defaultParams = {} } = gridChildrenConfig ?? {};
  const [params, setParams] = useState<Params>(defaultParams);
  const getSubItems = !!inHierarchyPath && !!params?.filters?.length;

  // in case that the entity has "eadNode" structure
  const eadNode = rootArchivalAid?.eadNode as EadNode | undefined;

  const eadItems = eadNode?.children ?? [];

  const { items, count, fetchMore } = useFetchList(
    listApi!,
    params,
    !getSubItems || item?.hasEadNode
  );

  useEffect(() => {
    if (inHierarchyPath) {
      const filter = getChildrenFilter?.(item);
      if (filter) {
        setParams(addFilter(defaultParams, filter));
      }
    } else {
      setParams({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getChildrenFilter, inHierarchyPath, item]);

  const withSubItems = items.length > 0 || eadItems.length > 0;
  const listItemClass = inHierarchyPath
    ? classes.listItemActive
    : classes.listItem;

  const hasChildren = item?.hasEadNode || item?.hasChildren;

  return (
    <>
      <Link to={itemLink} className={classes?.link}>
        <ListItem>
          {gridEntityConfig.icon && (
            <Icon className={classes.icon}>{gridEntityConfig.icon}</Icon>
          )}
          <ListItemText
            primary={trimLabel(itemLabel)}
            classes={{
              primary: listItemClass,
            }}
          />
          {hasChildren ? (
            inHierarchyPath ? (
              <ExpandLessIcon
                classes={{
                  root: listItemClass,
                }}
              />
            ) : (
              <ExpandMoreIcon
                classes={{
                  root: listItemClass,
                }}
              />
            )
          ) : null}
        </ListItem>
      </Link>
      {withSubItems && (
        <Collapse in={inHierarchyPath} timeout="auto" unmountOnExit>
          <List
            component="div"
            className={classes.nested}
            disablePadding
            dense={true}
            // subheader={
            //   <ListSubheader component="span" className={classes.subTitle}>
            //     {eadItems.length > 0 && level >= archivalAidLevel
            //       ? `EAD (${eadItems.length})`
            //       : `${gridConfig[level + 1].title} (${items.length})`}
            //   </ListSubheader>
            // }
          >
            {eadItems.length > 0 && level >= archivalAidLevel
              ? eadItems.map((ead) => {
                  return (
                    <GridLeftPanelEADItem
                      classes={classes}
                      key={ead.id}
                      item={ead}
                      level={level + 1}
                      hierarchyPath={hierarchyPath}
                      archivalAidLevel={archivalAidLevel}
                      rootArchivalAid={rootArchivalAid}
                      gridConfig={gridConfig}
                      GridLeftPanelItem={GridLeftPanelEADItem}
                    />
                  );
                })
              : items.map((subItem) => {
                  return (
                    <GridLeftPanelItem
                      classes={classes}
                      key={getId(subItem)}
                      item={subItem}
                      level={level + 1}
                      gridConfig={gridConfig}
                      hierarchyPath={hierarchyPath}
                      rootArchivalAid={rootArchivalAid}
                      archivalAidLevel={archivalAidLevel}
                      GridLeftPanelItem={
                        subItem?.hasEadNode
                          ? GridLeftPanelEADItem
                          : GridLeftPanelItem
                      }
                    />
                  );
                })}
          </List>
          {items.length < count && (
            <GridLeftPanelLoadMore fetchMore={fetchMore} />
          )}
        </Collapse>
      )}
    </>
  );
}
